import { css } from 'aphrodite';
import { parseAsString, useQueryState } from 'nuqs';
import { useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { Redirect } from 'react-router-dom';
import { useCoachingConversationCallView } from '../../../queries/AIConversation/useCoachingConversationCallView';
import { Button } from '../../../web/components-v2/Button';
import {
	AppDrawerContent,
	AppDrawerToolbarButton,
	CoffeeDrawerProvider,
	CoffeeDrawerToolbar,
	SideDrawer,
} from '../../../web/components-v2/Drawer/AppDrawer';
import { THEME } from '../../../web/components-v2/Theme/theme';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { useManagerPermissions } from '../../hooks/useManagerPermissions';
import '../../focused-view/theme/tokens.css';
import { AdherenceDrawerContent } from './AdherenceDrawerContent';
import { AICoachingReviewTopBar } from './AICoachingReviewTopBar';
import { ConversationInsights } from './ConversationInsights';
import { ConversationList } from './ConversationList';
import { MetadataPanel } from './MetadataPanel';
import { RepCoachingCards } from './RepCoachingCards';
import { styleSheet } from './styles';
import { TranscriptPanel } from './TranscriptPanel';
import { useCoachingReviewViewMode } from './useCoachingReviewViewMode';

const DRAWER_VALUE_ADHERENCE = 'ai-coaching-review-adherence';

const AdherenceToolbarIcon = () => (
	<svg
		width='16'
		height='16'
		viewBox='0 0 24 24'
		fill='none'
		stroke='currentColor'
		strokeWidth='2'
		strokeLinecap='round'
		strokeLinejoin='round'
		aria-hidden='true'
	>
		<path d='M9 4h6a1 1 0 0 1 1 1v1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h1V5a1 1 0 0 1 1-1z' />
		<path d='M9 13l2 2 4-4' />
	</svg>
);

export function CoffeeAICoachingReview() {
	// The aida app mounts the react-router-v5 nuqs adapter, so this reads/writes the hash query string.
	const [selectedConversationId, setSelectedConversationId] = useQueryState('convo', parseAsString);
	const [drawerValue, setDrawerValue] = useState(DRAWER_VALUE_ADHERENCE);
	const [viewMode, setViewMode] = useCoachingReviewViewMode();
	const isMobile = useMediaQuery({ maxWidth: THEME.breakpoints.md });
	const { hasManagerPermissions, isLoading: permissionsLoading } = useManagerPermissions();

	const isCards = viewMode === 'cards';

	// Cards mode has no account-wide list loaded, so resolve straight from the by-id fetch.
	const { notFound } = useCoachingConversationCallView(selectedConversationId, { preferLoadedList: !isCards });

	if (permissionsLoading) {
		return (
			<div data-focused-view className={`focused-view-root ${css(styleSheet.page)}`}>
				<div className={css(styleSheet.spinnerState)}>
					<LoadingSpinner type='large' />
				</div>
			</div>
		);
	}

	// The route's `onLoad` can't gate this — manager role resolves asynchronously.
	if (!hasManagerPermissions) {
		return <Redirect to='/' />;
	}

	const showList = !isCards && (!isMobile || !selectedConversationId);
	const showCards = isCards && !selectedConversationId;
	const showDetail = isCards ? Boolean(selectedConversationId) : !isMobile || Boolean(selectedConversationId);
	const showAdherenceDrawer = !isMobile && (!isCards || Boolean(selectedConversationId));

	return (
		<div data-focused-view className={`focused-view-root ${css(styleSheet.page)}`}>
			<AICoachingReviewTopBar viewMode={viewMode} onViewModeChange={setViewMode} />
			<CoffeeDrawerProvider value={drawerValue} onValueChange={setDrawerValue}>
				<div className={css(styleSheet.layout)}>
					{showList ? (
						<section className={css(styleSheet.panel, styleSheet.listPanel)}>
							<div className={css(styleSheet.panelHeader)}>
								<h2 className={css(styleSheet.panelTitle)}>AI Coaching Conversations</h2>
							</div>
							<ConversationList
								selectedConversationId={selectedConversationId}
								onSelect={id => setSelectedConversationId(id)}
							/>
						</section>
					) : null}

					{showCards ? (
						<RepCoachingCards
							selectedConversationId={selectedConversationId}
							onSelectCall={id => setSelectedConversationId(id)}
						/>
					) : null}

					{showDetail ? (
						<section className={css(styleSheet.panel, styleSheet.detailPanel)}>
							{isCards ? (
								<Button
									variant='ghost'
									size='sm'
									styles={[styleSheet.detailBack]}
									onClick={() => setSelectedConversationId(null)}
								>
									← Back to rep cards
								</Button>
							) : null}
							{notFound ? (
								<div className={css(styleSheet.detailNotFound)}>
									This conversation could not be found. It may have been deleted, or the link is invalid.
								</div>
							) : (
								<>
									<MetadataPanel conversationId={selectedConversationId} />
									<ConversationInsights conversationId={selectedConversationId} />
									<TranscriptPanel conversationId={selectedConversationId} />
								</>
							)}
						</section>
					) : null}

					{showAdherenceDrawer ? (
						<SideDrawer>
							<AppDrawerContent value={DRAWER_VALUE_ADHERENCE} unmountOnExit>
								<AdherenceDrawerContent conversationId={selectedConversationId} onClose={() => setDrawerValue('')} />
							</AppDrawerContent>
							<CoffeeDrawerToolbar>
								<AppDrawerToolbarButton value={DRAWER_VALUE_ADHERENCE} tooltip='Coaching adherence'>
									<AdherenceToolbarIcon />
								</AppDrawerToolbarButton>
							</CoffeeDrawerToolbar>
						</SideDrawer>
					) : null}
				</div>
			</CoffeeDrawerProvider>
		</div>
	);
}
